/**
 * Coleção `onboarding/` — etapa entre Leads e Clientes (fora de data/main).
 */

import {
  ONBOARDING_STATUS,
  ONBOARDING_STATUS_LABEL,
  ONBOARDING_STATUS_ORDER,
  ONBOARDING_ENTRY_SOURCE,
  normalizeOnboardingCrmEntrySource,
  ONBOARDING_ORIGIN_BIRTH,
  LAST_FILL_SOURCE,
} from './onboardingConstants.js';
import { LEAD_STATUS } from './leadConstants.js';
import {
  tsToMs,
  onlyDigits,
  normalizeTrilean,
  patchLead,
  markLeadConverted,
  setLeadStatus,
  createLeadManual,
} from './leadsRepo.js';
import { assertCrmAccess } from './crmAccess.js';
import { clientExistsInDataMain } from './crmDataMainClient.js';

const COL = 'onboarding';

function db() {
  assertCrmAccess();
  return window._fbDb;
}

function nowMs() {
  return Date.now();
}

function actorFromWindow() {
  const u = window._currentUser;
  return {
    uid: u?.uid || '',
    name: (u?.displayName || u?.email || '').trim() || 'Admin',
  };
}

/** @param {unknown} raw */
function normalizeObBirthMs(raw) {
  const ms = tsToMs(raw);
  if (ms != null) return ms;
  const s = String(raw ?? '').trim();
  if (!s) return null;
  // BR: DD/MM/AAAA (separadores / . -) — formato que o site de onboarding envia.
  const br = s.match(/^(\d{1,2})[/.-](\d{1,2})[/.-](\d{4})$/);
  if (br) {
    const d = new Date(`${br[3]}-${br[2].padStart(2, '0')}-${br[1].padStart(2, '0')}T12:00:00`);
    return Number.isNaN(d.getTime()) ? null : d.getTime();
  }
  const d = new Date(s.includes('T') ? s : `${s}T12:00:00`);
  return Number.isNaN(d.getTime()) ? null : d.getTime();
}

/** @param {unknown} raw — leitura Firestore */
function normalizeOnboardingPlanoRead(raw) {
  if (raw == null || typeof raw !== 'object' || Array.isArray(raw)) return {};
  return { .../** @type {Record<string, unknown>} */ (raw) };
}

/**
 * @param {unknown} v — escrita Firestore (null se vazio)
 */
function planoForFirestore(v) {
  if (v == null || typeof v !== 'object' || Array.isArray(v)) return null;
  const o = { .../** @type {Record<string, unknown>} */ (v) };
  return Object.keys(o).length ? o : null;
}

/**
 * @param {Record<string, unknown>} raw
 * @param {string} id
 */
export function normalizeOnboardingDoc(raw, id) {
  const waLegacy = String(raw.whatsapp ?? '');
  const waMasked = String(raw.whatsappMasked ?? '');
  const waDigits = onlyDigits(raw.whatsappDigits) || onlyDigits(waLegacy);
  const whatsappDisplay = waMasked.trim() || waLegacy.trim();
  const nomeCompletoRaw = String(raw.nomeCompleto ?? '').trim();
  const nomeAlias = String(raw.nome ?? '').trim();
  const dataNascimento =
    normalizeObBirthMs(raw.dataNascimento) ?? normalizeObBirthMs(raw.aniversario);
  return {
    id,
    nomeCompleto: nomeCompletoRaw || nomeAlias,
    dataNascimento,
    cpf: String(raw.cpf ?? ''),
    cnpj: String(raw.cnpj ?? ''),
    email: String(raw.email ?? ''),
    whatsapp: whatsappDisplay,
    whatsappMasked: waMasked.trim(),
    whatsappDigits: waDigits,
    enderecoResidencial: String(raw.enderecoResidencial ?? raw.endResidencial ?? ''),
    enderecoProfissional: String(raw.enderecoProfissional ?? raw.endProfissional ?? ''),
    cidade: String(raw.cidade ?? ''),
    estado: String(raw.estado ?? ''),
    especialidade: String(raw.especialidade ?? ''),
    crm: String(raw.crm ?? ''),
    rqe: String(raw.rqe ?? ''),
    status: ONBOARDING_STATUS_ORDER.includes(/** @type {string} */ (raw.status))
      ? raw.status
      : ONBOARDING_STATUS.NOVO,
    createdAt: tsToMs(raw.createdAt),
    updatedAt: tsToMs(raw.updatedAt),
    archived: raw.archived === true,
    archivedAt: tsToMs(raw.archivedAt),
    convertedAt: tsToMs(raw.convertedAt),
    convertedClientId: String(raw.convertedClientId ?? ''),
    formType: String(raw.formType ?? '').trim(),
    receivedAt: tsToMs(raw.receivedAt),
    origem: String(raw.origem ?? '').trim(),
    privacyConsent: normalizeTrilean(raw.privacyConsent),
    privacyConsentAt: tsToMs(raw.privacyConsentAt),
    privacyConsentSource: String(raw.privacyConsentSource ?? '').trim(),
    marketingConsent: normalizeTrilean(raw.marketingConsent),
    marketingConsentAt: tsToMs(raw.marketingConsentAt),
    marketingConsentSource: String(raw.marketingConsentSource ?? '').trim(),
    userAgent: String(raw.userAgent ?? '').trim(),
    source: String(raw.source ?? raw.canal ?? '').trim(),
    channel: String(raw.channel ?? '').trim(),
    leadId: String(raw.leadId ?? '').trim(),
    crmEntrySource: normalizeOnboardingCrmEntrySource(raw.crmEntrySource),
    plano: normalizeOnboardingPlanoRead(raw.plano),
    tipoFinal: String(raw.tipoFinal ?? '').trim(),
    reviewedAt: tsToMs(raw.reviewedAt),
    dashboardSelfFillPending: raw.dashboardSelfFillPending === true,
    clientInviteLastSentAt: tsToMs(raw.clientInviteLastSentAt),
    clientInviteLinkOpenedAt: tsToMs(raw.clientInviteLinkOpenedAt),
    onboardingCreationMode:
      raw.onboardingCreationMode === 'client_invite' || raw.onboardingCreationMode === 'manual'
        ? raw.onboardingCreationMode
        : null,
    createdVia: String(raw.createdVia ?? '').trim(),
    /** Fase 2 — canónico: como nasceu (imutável). */
    onboardingOriginBirth: String(raw.onboardingOriginBirth ?? '').trim(),
    /** Fase 2 — canónico: última origem de preenchimento. */
    lastFillSource: String(raw.lastFillSource ?? '').trim(),
  };
}

export function subscribeOnboardingList(onData, onError) {
  const database = db();
  if (!database || !window._fbCollection || !window._fbQuery || !window._fbOrderBy || !window._fbOnSnapshot) {
    onData([]);
    return () => {};
  }
  const col = window._fbCollection(database, COL);
  const q = window._fbQuery(col, window._fbOrderBy('createdAt', 'desc'));
  return window._fbOnSnapshot(
    q,
    snap => {
      onData(snap.docs.map(d => normalizeOnboardingDoc(d.data(), d.id)));
    },
    err => {
      console.warn('[CRM onboarding] subscribe', err);
      if (typeof onError === 'function') onError(err);
      onData([]);
    },
  );
}

export function subscribeOnboardingTimeline(obId, onData) {
  const database = db();
  if (!obId || !database || !window._fbCollection || !window._fbQuery || !window._fbOrderBy || !window._fbOnSnapshot) {
    onData([]);
    return () => {};
  }
  const col = window._fbCollection(database, COL, obId, 'timeline');
  const q = window._fbQuery(col, window._fbOrderBy('at', 'desc'));
  return window._fbOnSnapshot(
    q,
    snap => onData(snap.docs.map(d => ({ id: d.id, ...d.data() }))),
    () => onData([]),
  );
}

/**
 * Escrita Firestore a partir do CRM (dashboard). Campos de identidade, consentimento e
 * endereço devem manter-se alinhados ao slice HTTP em `shared/crmOnboardingFirestoreContract.cjs`
 * (`buildOnboardingWebsiteFormFields`): mesmos nomes e semântica; aqui entrada interna
 * (sem truncagem por comprimento máximo do body público).
 */
function buildPayload(p, t) {
  const email = String(p.email || '').trim().toLowerCase();
  const waPlain = String(p.whatsapp || '').trim();
  const ft = String(p.formType || '').trim();
  const og = String(p.origem || '').trim();
  const dataNascimentoFromNum =
    typeof p.dataNascimento === 'number' && Number.isFinite(p.dataNascimento) ? p.dataNascimento : null;
  const dataNascimento = dataNascimentoFromNum ?? normalizeObBirthMs(p.aniversario);
  return {
    nomeCompleto: String(p.nomeCompleto || p.nome || '').trim(),
    dataNascimento,
    cpf: String(p.cpf || '').trim(),
    cnpj: String(p.cnpj || '').trim(),
    email,
    whatsapp: waPlain,
    whatsappMasked: String(p.whatsappMasked || '').trim(),
    whatsappDigits: onlyDigits(p.whatsappDigits) || onlyDigits(waPlain),
    enderecoResidencial: String(p.enderecoResidencial || p.endResidencial || '').trim(),
    enderecoProfissional: String(p.enderecoProfissional || p.endProfissional || '').trim(),
    cidade: String(p.cidade || '').trim(),
    estado: String(p.estado || '').trim(),
    especialidade: String(p.especialidade || '').trim(),
    crm: String(p.crm || '').trim(),
    rqe: String(p.rqe || '').trim(),
    status:
      p.status && ONBOARDING_STATUS_ORDER.includes(/** @type {string} */ (p.status))
        ? p.status
        : ONBOARDING_STATUS.NOVO,
    createdAt: t,
    updatedAt: t,
    archived: false,
    archivedAt: null,
    convertedAt: null,
    convertedClientId: null,
    formType: ft || null,
    receivedAt: tsToMs(p.receivedAt),
    origem: og || null,
    privacyConsent: normalizeTrilean(p.privacyConsent),
    privacyConsentAt: tsToMs(p.privacyConsentAt),
    privacyConsentSource: String(p.privacyConsentSource || '').trim() || null,
    marketingConsent: normalizeTrilean(p.marketingConsent),
    marketingConsentAt: tsToMs(p.marketingConsentAt),
    marketingConsentSource: String(p.marketingConsentSource || '').trim() || null,
    userAgent: String(p.userAgent || '').trim() || null,
    source: String(p.source || p.canal || '').trim() || null,
    channel: String(p.channel || '').trim() || null,
    leadId: String(p.leadId || '').trim() || null,
    crmEntrySource: (() => {
      const s = normalizeOnboardingCrmEntrySource(p.crmEntrySource);
      return s || null;
    })(),
    plano: planoForFirestore(p.plano),
    tipoFinal: String(p.tipoFinal || '').trim() || null,
    reviewedAt: tsToMs(p.reviewedAt),
    ...(p.dashboardSelfFillPending === true ? { dashboardSelfFillPending: true } : {}),
    ...(p.onboardingCreationMode === 'client_invite' || p.onboardingCreationMode === 'manual'
      ? { onboardingCreationMode: p.onboardingCreationMode }
      : {}),
    ...(tsToMs(p.clientInviteLastSentAt) != null
      ? { clientInviteLastSentAt: tsToMs(p.clientInviteLastSentAt) }
      : {}),
    ...(tsToMs(p.clientInviteLinkOpenedAt) != null
      ? { clientInviteLinkOpenedAt: tsToMs(p.clientInviteLinkOpenedAt) }
      : {}),
    ...(String(p.createdVia || '').trim()
      ? { createdVia: String(p.createdVia).trim().slice(0, 48) }
      : {}),
    onboardingOriginBirth: String(p.onboardingOriginBirth || '').trim().slice(0, 48) || null,
    lastFillSource: String(p.lastFillSource || '').trim().slice(0, 48) || null,
  };
}

/**
 * Monta o payload inicial de onboarding a partir de um lead normalizado (`normalizeLeadDoc`).
 * Só inclui campos que o lead costuma ter; não inventa dados operacionais.
 * Fluxo separado de onboarding directo — ambos convergem em `createOnboardingManual` / `buildPayload`.
 *
 * @param {Record<string, unknown>} lead
 * @returns {Record<string, unknown>}
 */
export function leadToOnboardingInitialPayload(lead) {
  const nome = String(lead.nome || '').trim();
  const origem =
    String(lead.origem || '').trim() ||
    String(lead.origemLead || '').trim() ||
    '';
  return {
    nomeCompleto: nome,
    email: String(lead.email || '').trim().toLowerCase(),
    whatsapp: String(lead.whatsapp || '').trim(),
    whatsappMasked: String(lead.whatsappMasked || '').trim(),
    whatsappDigits: String(lead.whatsappDigits || '').trim(),
    cidade: String(lead.cidade || '').trim(),
    estado: String(lead.estado || '').trim(),
    especialidade: String(lead.especialidade || '').trim(),
    origem: origem || undefined,
    source: String(lead.source || '').trim(),
    channel: String(lead.channel || '').trim(),
    formType: String(lead.formType || '').trim(),
    receivedAt: lead.receivedAt,
    privacyConsent: lead.privacyConsent,
    privacyConsentAt: lead.privacyConsentAt,
    privacyConsentSource: String(lead.privacyConsentSource || '').trim(),
    marketingConsent: lead.marketingConsent,
    marketingConsentAt: lead.marketingConsentAt,
    marketingConsentSource: String(lead.marketingConsentSource || '').trim(),
    userAgent: String(lead.userAgent || '').trim(),
  };
}

/**
 * Ponto único para resolver **dados iniciais** do onboarding no dashboard (e, no futuro, convite por link).
 * - `from_lead` → `leadToOnboardingInitialPayload(lead)`; exige `lead`.
 * - `direct` → payload vazio; sem `leadId`.
 * - `invite` → site: `resolveOnboardingInvite` / `submitOnboardingWithInvite`. CANÓNICO (Fase 4): stub no dashboard
 *   (`createOnboardingClientInviteFromDashboard`) + `createOnboardingInvite` com `onboardingId`. LEGADO: convite só com
 *   `leadId` (onboarding criado no submit do site) — evitar em fluxos novos.
 *
 * Ambos os fluxos válidos devem convergir no mesmo contrato via `createOnboardingManual` / `buildPayload`.
 *
 * @param {{ source?: string, lead?: Record<string, unknown> }} input
 * @returns {{ payload: Record<string, unknown>, leadId: string|null, crmEntrySource: string }}
 */
export function resolveOnboardingInitialPayload(input) {
  const inv = input && typeof input === 'object' ? input : {};
  const source = inv.source;
  if (source === ONBOARDING_ENTRY_SOURCE.FROM_LEAD) {
    const lead = inv.lead;
    if (!lead || typeof lead !== 'object') {
      throw new Error('resolveOnboardingInitialPayload: `lead` é obrigatório quando source é from_lead');
    }
    const leadId = String(lead.id || '').trim();
    return {
      payload: leadToOnboardingInitialPayload(lead),
      leadId: leadId || null,
      crmEntrySource: ONBOARDING_ENTRY_SOURCE.FROM_LEAD,
    };
  }
  if (source === ONBOARDING_ENTRY_SOURCE.DIRECT) {
    return {
      payload: {},
      leadId: null,
      crmEntrySource: ONBOARDING_ENTRY_SOURCE.DIRECT,
    };
  }
  if (source === ONBOARDING_ENTRY_SOURCE.INVITE) {
    throw new Error(
      'resolveOnboardingInitialPayload: fluxo invite é no site (Cloud Functions resolveOnboardingInvite / submitOnboardingWithInvite), não no dashboard.',
    );
  }
  if (source != null && source !== '') {
    throw new Error(`resolveOnboardingInitialPayload: source desconhecido: ${String(source)}`);
  }
  return {
    payload: {},
    leadId: null,
    crmEntrySource: ONBOARDING_ENTRY_SOURCE.DIRECT,
  };
}

/**
 * @param {string} leadId
 * @returns {Promise<string|null>}
 */
async function findOnboardingIdByLeadIdQuery(leadId) {
  const database = db();
  if (
    !database ||
    !leadId ||
    !window._fbCollection ||
    !window._fbQuery ||
    !window._fbWhere ||
    !window._fbLimit ||
    !window._fbGetDocs
  ) {
    return null;
  }
  const col = window._fbCollection(database, COL);
  const q = window._fbQuery(col, window._fbWhere('leadId', '==', leadId), window._fbLimit(1));
  const snap = await window._fbGetDocs(q);
  const doc0 = snap.docs[0];
  return doc0 ? doc0.id : null;
}

/**
 * Resolve onboarding existente: `lead.onboardingId` válido ou doc com `leadId`.
 * @param {Record<string, unknown>} lead
 * @returns {Promise<{ onboardingId: string, reason: 'hinted'|'query' }|null>}
 */
async function resolveExistingOnboardingForLead(lead) {
  const leadId = String(lead.id || '').trim();
  if (!leadId) return null;
  const database = db();
  const hinted = String(lead.onboardingId || '').trim();

  if (hinted && database && window._fbDoc && window._fbGetDoc) {
    const ref = window._fbDoc(database, COL, hinted);
    const snap = await window._fbGetDoc(ref);
    if (snap.exists()) {
      const data = snap.data() || {};
      const docLead = String(data.leadId || '').trim();
      if (!docLead || docLead === leadId) {
        return { onboardingId: hinted, reason: 'hinted' };
      }
    }
  }

  const fromQuery = await findOnboardingIdByLeadIdQuery(leadId);
  if (fromQuery) return { onboardingId: fromQuery, reason: 'query' };
  return null;
}

/**
 * Cria onboarding a partir de um lead (dashboard), com vínculo bidirecional e anti-duplicação.
 *
 * @param {Record<string, unknown>} lead — resultado de `normalizeLeadDoc`
 * @returns {Promise<{ onboardingId: string, alreadyLinked: boolean, repairedLeadLink?: boolean }>}
 */
export async function createOnboardingFromLead(lead) {
  const leadId = String(lead?.id || '').trim();
  if (!leadId) throw new Error('Lead inválido');
  const email = String(lead?.email || '').trim().toLowerCase();
  const nome = String(lead?.nome || '').trim();
  const wa = String(lead?.whatsapp || '').trim();
  if (!email && !nome && !wa) {
    throw new Error('Indique pelo menos nome, e-mail ou WhatsApp no lead para criar onboarding');
  }

  const existing = await resolveExistingOnboardingForLead(lead);
  if (existing) {
    const obId = existing.onboardingId;
    const leadHint = String(lead.onboardingId || '').trim();
    const needsLeadPatch = leadHint !== obId || lead.sentToOnboardingAt == null;
    if (needsLeadPatch) {
      const t = nowMs();
      await patchLead(
        leadId,
        {
          onboardingId: obId,
          sentToOnboardingAt: lead.sentToOnboardingAt != null ? lead.sentToOnboardingAt : t,
          status: LEAD_STATUS.ONBOARDING,
        },
        existing.reason === 'query' ? 'Vínculo com onboarding registado' : 'Dados de vínculo onboarding actualizados',
        { onboardingId: obId, fromLead: leadId },
        'edit',
      );
      return {
        onboardingId: obId,
        alreadyLinked: true,
        repairedLeadLink: existing.reason === 'query' || leadHint !== obId,
      };
    }
    if (lead.status !== LEAD_STATUS.ONBOARDING && lead.status !== LEAD_STATUS.CONVERTIDO) {
      try {
        await setLeadStatus(leadId, LEAD_STATUS.ONBOARDING);
      } catch (e) {
        console.warn('[CRM] createOnboardingFromLead: status onboarding não sincronizado', e);
      }
    }
    return { onboardingId: obId, alreadyLinked: true };
  }

  const { payload: initial, crmEntrySource } = resolveOnboardingInitialPayload({
    source: ONBOARDING_ENTRY_SOURCE.FROM_LEAD,
    lead,
  });
  const obId = await createOnboardingManual(
    { ...initial, email, leadId, crmEntrySource },
    {
      timelineMessage: 'Onboarding criado a partir do lead',
      timelineMeta: { leadId, crmEntrySource: ONBOARDING_ENTRY_SOURCE.FROM_LEAD },
    },
  );

  const t = nowMs();
  await patchLead(
    leadId,
    { onboardingId: obId, sentToOnboardingAt: t, status: LEAD_STATUS.ONBOARDING },
    'Enviado para onboarding',
    { onboardingId: obId },
    'edit',
  );

  return { onboardingId: obId, alreadyLinked: false };
}

/**
 * Dashboard — modo «cliente preenche no site»: cria lead stub, onboarding com `leadId`, depois vínculo no lead.
 * O convite HTTPS deve ser gerado com `onboardingId` (o callable só com `leadId` exige lead ainda sem onboarding).
 *
 * @param {{ email: string, nomeProvisorio?: string, whatsapp?: string }} input
 * @param {{ notify?: boolean }} [opts]
 * @returns {Promise<{ leadId: string, onboardingId: string }>}
 */
export async function createOnboardingClientInviteFromDashboard(input, opts = {}) {
  // E-mail é opcional: o cliente preenche no site pelo link do convite.
  const emailNorm = String(input?.email || '').trim().toLowerCase();
  const wa = String(input?.whatsapp || '').trim();
  const nomeIn = String(input?.nomeProvisorio || '').trim();
  const local =
    emailNorm.includes('@') ? emailNorm.split('@')[0].replace(/[.+_]/g, ' ').trim() : '';
  const displayNome = nomeIn || local || 'Cliente (convite)';

  const leadId = await createLeadManual(
    {
      nome: displayNome,
      email: emailNorm,
      whatsapp: wa,
      origem: 'dashboard_convite_cliente',
      origem_pagina: 'dashboard',
      entrada: 'manual',
      status: LEAD_STATUS.NOVO,
      createdVia: 'client_invite',
      pendingClientSelfFill: true,
    },
    {
      notify: opts.notify !== false,
      timelineMessage: 'Lead criado (convite — aguarda preenchimento no site)',
      sourceMeta: { createdVia: 'client_invite', flow: 'dashboard_client_invite' },
    },
  );

  const stubLead = {
    id: leadId,
    nome: displayNome,
    email: emailNorm,
    whatsapp: wa,
    origem: 'dashboard_convite_cliente',
  };

  const { payload: initial } = resolveOnboardingInitialPayload({
    source: ONBOARDING_ENTRY_SOURCE.FROM_LEAD,
    lead: stubLead,
  });

  // Campos extras (ex.: convite a partir de um cliente existente) — pré-preenchem o stub
  // para o cliente não reescrever no site. Só inclui os que vierem preenchidos.
  const extra = {};
  for (const k of ['cpf', 'cnpj', 'cidade', 'estado', 'especialidade', 'enderecoResidencial', 'enderecoProfissional', 'crm', 'rqe']) {
    const v = String(input?.[k] || '').trim();
    if (v) extra[k] = v;
  }
  if (input?.clientId) extra.convertedClientId = String(input.clientId).trim();

  const onboardingId = await createOnboardingManual(
    {
      ...initial,
      ...extra,
      email: emailNorm,
      whatsapp: wa,
      nomeCompleto: displayNome,
      leadId,
      crmEntrySource: ONBOARDING_ENTRY_SOURCE.FROM_LEAD,
      onboardingCreationMode: 'client_invite',
      dashboardSelfFillPending: true,
      createdVia: 'client_invite',
    },
    {
      timelineMessage: 'Onboarding criado — aguarda preenchimento pelo cliente (convite)',
      timelineMeta: { leadId, mode: 'client_invite_dashboard' },
    },
  );

  const t = nowMs();
  await patchLead(
    leadId,
    {
      onboardingId,
      sentToOnboardingAt: t,
      status: LEAD_STATUS.ONBOARDING,
    },
    'Vinculado ao onboarding (convite)',
    { onboardingId },
    'edit',
  );

  return { leadId, onboardingId };
}

/**
 * Convite pré-preenchido a partir de um LEAD existente (painel do lead no CRM).
 * Igual ao fluxo FromDashboard, mas SEM criar outro lead: o stub «cliente preenche
 * no site» nasce vinculado a este lead, pré-preenchido com os dados dele, e o lead
 * segue para a etapa de onboarding. O link é gerado em seguida pelo callable
 * `createOnboardingInvite` com este `onboardingId`.
 */
export async function createOnboardingClientInviteFromLead(lead) {
  const leadId = String(lead?.id || '').trim();
  if (!leadId) throw new Error('Lead inválido');
  // E-mail é opcional: o cliente preenche no site. Usamos o que houver como pré-preenchimento.
  const existingObId = String(lead?.onboardingId || '').trim();
  if (existingObId) return { leadId, onboardingId: existingObId, alreadyLinked: true };

  const { payload: initial } = resolveOnboardingInitialPayload({
    source: ONBOARDING_ENTRY_SOURCE.FROM_LEAD,
    lead,
  });

  const onboardingId = await createOnboardingManual(
    {
      ...initial,
      leadId,
      crmEntrySource: ONBOARDING_ENTRY_SOURCE.FROM_LEAD,
      onboardingCreationMode: 'client_invite',
      dashboardSelfFillPending: true,
      createdVia: 'client_invite',
    },
    {
      timelineMessage: 'Onboarding criado — aguarda preenchimento pelo cliente (convite a partir do lead)',
      timelineMeta: { leadId, mode: 'client_invite_from_lead' },
    },
  );

  await patchLead(
    leadId,
    {
      onboardingId,
      sentToOnboardingAt: nowMs(),
      status: LEAD_STATUS.ONBOARDING,
    },
    'Vinculado ao onboarding (convite)',
    { onboardingId },
    'edit',
  );

  return { leadId, onboardingId, alreadyLinked: false };
}

/**
 * @param {Record<string, unknown>} p
 * @param {{ timelineMessage?: string, timelineMeta?: Record<string, unknown> }} [opts]
 */
export async function createOnboardingManual(p, opts = {}) {
  const database = db();
  if (!database) throw new Error('Firebase não disponível');
  const email = String(p.email || '').trim().toLowerCase();
  const nome = String(p.nomeCompleto || p.nome || '').trim();
  const wa = String(p.whatsapp || '').trim();
  if (!email && !nome && !wa) {
    throw new Error('Indique pelo menos nome completo, e-mail ou WhatsApp');
  }
  const srcNorm = normalizeOnboardingCrmEntrySource(p.crmEntrySource);
  let onboardingOriginBirth = String(p.onboardingOriginBirth || '').trim();
  let lastFillSource = String(p.lastFillSource || '').trim();
  if (!onboardingOriginBirth) {
    if (p.onboardingCreationMode === 'client_invite') {
      onboardingOriginBirth = ONBOARDING_ORIGIN_BIRTH.DASHBOARD_CLIENT_INVITE;
    } else if (srcNorm === ONBOARDING_ENTRY_SOURCE.FROM_LEAD) {
      onboardingOriginBirth = ONBOARDING_ORIGIN_BIRTH.FROM_LEAD;
    } else {
      onboardingOriginBirth = ONBOARDING_ORIGIN_BIRTH.MANUAL;
    }
  }
  if (!lastFillSource) {
    lastFillSource = LAST_FILL_SOURCE.DASHBOARD_CREATE;
  }
  let leadId = String(p.leadId || '').trim();
  let autoCreatedLead = false;
  if (!leadId) {
    autoCreatedLead = true;
    console.info('[CRM] createOnboardingManual: lead automático (fluxo manual sem leadId)');
    const nomeLead = nome || (email.includes('@') ? email.split('@')[0] : '') || 'Cliente';
    leadId = await createLeadManual(
      {
        nome: nomeLead,
        email,
        whatsapp: wa,
        cidade: String(p.cidade || '').trim(),
        estado: String(p.estado || '').trim(),
        especialidade: String(p.especialidade || '').trim(),
        origem: 'dashboard_onboarding',
        origem_pagina: 'dashboard',
        entrada: 'manual',
        status: LEAD_STATUS.NOVO,
      },
      {
        notify: false,
        timelineMessage: 'Lead criado automaticamente com onboarding (dashboard)',
        sourceMeta: { autoFromCreateOnboardingManual: true },
      },
    );
  }
  const t = nowMs();
  const payload = buildPayload(
    { ...p, email, leadId, onboardingOriginBirth, lastFillSource },
    t,
  );
  const col = window._fbCollection(database, COL);
  const ref = await window._fbAddDoc(col, payload);
  const evCol = window._fbCollection(database, COL, ref.id, 'timeline');
  const { uid, name } = actorFromWindow();
  await window._fbAddDoc(evCol, {
    kind: 'created',
    at: window._fbServerTimestamp(),
    actorUid: uid,
    actorName: name,
    message: opts.timelineMessage || 'Onboarding criado',
    meta: opts.timelineMeta || {},
  });
  if (autoCreatedLead) {
    await patchLead(
      leadId,
      {
        onboardingId: ref.id,
        sentToOnboardingAt: t,
        status: LEAD_STATUS.ONBOARDING,
      },
      'Vinculado ao onboarding (criação manual)',
      { onboardingId: ref.id },
      'edit',
    );
  }
  return ref.id;
}

/**
 * @param {string} obId
 * @param {Record<string, unknown>} patch
 * @param {string} timelineMessage
 * @param {object} [meta]
 * @param {string} [kind]
 */
export async function patchOnboarding(obId, patch, timelineMessage, meta = {}, kind = 'edit') {
  const database = db();
  if (!obId || !database) throw new Error('Dados inválidos');
  const { uid, name } = actorFromWindow();
  const ref = window._fbDoc(database, COL, obId);
  await window._fbUpdateDoc(ref, {
    ...patch,
    updatedAt: nowMs(),
  });
  const evCol = window._fbCollection(database, COL, obId, 'timeline');
  await window._fbAddDoc(evCol, {
    kind,
    at: window._fbServerTimestamp(),
    actorUid: uid,
    actorName: name,
    message: timelineMessage,
    meta: meta || {},
  });
}

export async function setOnboardingStatus(obId, status) {
  if (!ONBOARDING_STATUS_ORDER.includes(status)) throw new Error('Status inválido');
  const label = ONBOARDING_STATUS_LABEL[status] || status;
  await patchOnboarding(obId, { status }, `Status alterado para ${label}`, { toStatus: status }, 'status');
}

export async function addOnboardingNote(obId, text) {
  const t = String(text || '').trim();
  if (!t) throw new Error('Observação vazia');
  await patchOnboarding(obId, {}, 'Observação adicionada', { note: t }, 'note');
}

export async function archiveOnboarding(obId) {
  const t = nowMs();
  await patchOnboarding(obId, { archived: true, archivedAt: t }, 'Registo arquivado', {}, 'archive');
}

export async function unarchiveOnboarding(obId) {
  await patchOnboarding(obId, { archived: false, archivedAt: null }, 'Registo desarquivado', {}, 'archive');
}

/**
 * Regista na timeline dos onboardings vinculados que o cliente foi excluído da aba Clientes,
 * gravando quem excluiu (via patchOnboarding → actorFromWindow). Mantém o vínculo
 * (convertedClientId) para o CRM detectar a exclusão e reoferecer a conversão.
 * @param {string} clientId
 * @param {string} [clientName]
 * @returns {Promise<number>} quantos onboardings receberam o evento
 */
export async function logClientDeletionOnLinkedOnboardings(clientId, clientName = '') {
  const database = db();
  const cid = String(clientId || '').trim();
  if (!database || !cid) return 0;
  const q = window._fbQuery(
    window._fbCollection(database, COL),
    window._fbWhere('convertedClientId', '==', cid),
  );
  const snap = await window._fbGetDocs(q);
  const ids = [];
  snap.forEach(d => ids.push(d.id));
  const nm = String(clientName || '').trim();
  const msg = nm ? `Cliente «${nm}» excluído da aba Clientes` : 'Cliente excluído da aba Clientes';
  for (const obId of ids) {
    // patch vazio: preserva convertedClientId; só acrescenta o evento com o autor.
    await patchOnboarding(obId, {}, msg, { clientId: cid, clientName: nm }, 'client_deleted');
  }
  return ids.length;
}

/**
 * Backfill: regista (uma vez) que o cliente vinculado deixou de existir na aba Clientes,
 * para onboardings cujo cliente foi excluído ANTES desta função existir (sem autor/data reais).
 * Idempotente: só grava se ainda não houver um evento 'client_deleted' na timeline.
 * @param {string} obId
 * @param {string} clientId
 * @returns {Promise<boolean>} true se gravou agora
 */
export async function logClientMissingDetected(obId, clientId) {
  const database = db();
  const id = String(obId || '').trim();
  if (!database || !id) return false;
  const evCol = window._fbCollection(database, COL, id, 'timeline');
  // Evita duplicar: se já existe um evento de exclusão, não grava.
  const existing = await window._fbGetDocs(
    window._fbQuery(evCol, window._fbWhere('kind', '==', 'client_deleted'), window._fbLimit(1)),
  );
  if (!existing.empty) return false;
  await window._fbAddDoc(evCol, {
    kind: 'client_deleted',
    at: window._fbServerTimestamp(),
    actorUid: '',
    actorName: 'Sistema',
    message: 'Cliente vinculado excluído da aba Clientes',
    meta: { clientId: String(clientId || '').trim(), detected: true },
  });
  return true;
}

/**
 * Marca onboarding como independente (decisão manual no CRM para suprimir sugestão de vínculo).
 * @param {string} obId
 */
export async function markOnboardingIndependent(obId) {
  await patchOnboarding(
    obId,
    {
      leadAssociationDecision: 'independente',
      leadAssociationReviewedAt: nowMs(),
    },
    'Mantido como onboarding independente (sem vínculo com lead)',
    {},
    'edit',
  );
}

/**
 * Vínculo assistido onboarding -> lead existente.
 * Segurança:
 * - não vincula onboarding já vinculado;
 * - não substitui lead já vinculado a outro onboarding;
 * - não apaga registos;
 * - merge opcional e conservador (somente campos de contato/localidade/especialidade).
 *
 * @param {{ onboardingId: string, leadId: string, mergeBasicFields?: boolean }} input
 */
export async function associateOnboardingToLead(input) {
  const database = db();
  if (!database) throw new Error('Firebase não disponível');
  const onboardingId = String(input?.onboardingId || '').trim();
  const leadId = String(input?.leadId || '').trim();
  if (!onboardingId || !leadId) throw new Error('Dados inválidos');
  const mergeBasicFields = input?.mergeBasicFields !== false;

  const obRef = window._fbDoc(database, COL, onboardingId);
  const leadRef = window._fbDoc(database, 'leads', leadId);
  const [obSnap, leadSnap] = await Promise.all([window._fbGetDoc(obRef), window._fbGetDoc(leadRef)]);
  if (!obSnap.exists()) throw new Error('Onboarding não encontrado');
  if (!leadSnap.exists()) throw new Error('Lead não encontrado');

  const ob = normalizeOnboardingDoc(obSnap.data(), onboardingId);
  const lead = {
    ...leadSnap.data(),
    id: leadId,
  };

  const currentLeadId = String(ob.leadId || '').trim();
  if (currentLeadId && currentLeadId !== leadId) {
    throw new Error('Este onboarding já está vinculado a outro lead.');
  }
  const leadOnboardingId = String(lead.onboardingId || '').trim();
  if (leadOnboardingId && leadOnboardingId !== onboardingId) {
    throw new Error('Lead já possui outro onboarding vinculado.');
  }

  const leadPatch = {
    onboardingId,
    sentToOnboardingAt: tsToMs(lead.sentToOnboardingAt) ?? nowMs(),
    status: LEAD_STATUS.ONBOARDING,
    leadAssociationDecision: null,
    leadAssociationReviewedAt: nowMs(),
  };

  if (mergeBasicFields) {
    const obNome = String(ob.nomeCompleto || '').trim();
    const obWa = String(ob.whatsapp || '').trim();
    const obWaMasked = String(ob.whatsappMasked || '').trim();
    const obWaDigits = String(ob.whatsappDigits || '').trim();
    const obCidade = String(ob.cidade || '').trim();
    const obEstado = String(ob.estado || '').trim();
    const obEsp = String(ob.especialidade || '').trim();
    const obEmail = String(ob.email || '').trim().toLowerCase();

    if (!String(lead.nome || '').trim() && obNome) leadPatch.nome = obNome;
    if (!String(lead.email || '').trim() && obEmail) leadPatch.email = obEmail;
    if (!String(lead.whatsapp || '').trim() && obWa) leadPatch.whatsapp = obWa;
    if (!String(lead.whatsappMasked || '').trim() && obWaMasked) leadPatch.whatsappMasked = obWaMasked;
    if (!String(lead.whatsappDigits || '').trim() && obWaDigits) leadPatch.whatsappDigits = obWaDigits;
    if (!String(lead.cidade || '').trim() && obCidade) leadPatch.cidade = obCidade;
    if (!String(lead.estado || '').trim() && obEstado) leadPatch.estado = obEstado;
    if (!String(lead.especialidade || '').trim() && obEsp) leadPatch.especialidade = obEsp;
  }

  const obPatch = {
    leadId,
    crmEntrySource: ONBOARDING_ENTRY_SOURCE.FROM_LEAD,
    leadAssociationDecision: 'associado',
    leadAssociationReviewedAt: nowMs(),
  };

  await Promise.all([
    patchOnboarding(
      onboardingId,
      obPatch,
      'Onboarding associado a lead existente',
      { leadId, mergeBasicFields },
      'edit',
    ),
    patchLead(
      leadId,
      leadPatch,
      'Onboarding associado ao lead',
      { onboardingId, mergeBasicFields },
      'edit',
    ),
  ]);
}

/**
 * Exclui onboarding diretamente (sem arquivar), removendo timeline.
 * Se houver lead vinculado com `onboardingId` igual, remove o vínculo no lead.
 * Com `convertedClientId`: só bloqueia se o cliente ainda existir em `data/main.clients`.
 *
 * @param {string} obId
 */
export async function deleteOnboardingPermanent(obId) {
  const database = db();
  if (!obId || !database) throw new Error('Dados inválidos');
  if (!window._fbGetDoc || !window._fbDeleteDoc || !window._fbGetDocs || !window._fbWriteBatch) {
    throw new Error('Firebase incompleto');
  }

  const obRef = window._fbDoc(database, COL, obId);
  const obSnap = await window._fbGetDoc(obRef);
  if (!obSnap.exists()) throw new Error('Onboarding não encontrado');
  const ob = normalizeOnboardingDoc(obSnap.data(), obId);
  const convertedId = String(ob.convertedClientId || '').trim();
  if (convertedId && (await clientExistsInDataMain(convertedId))) {
    throw new Error('Este onboarding está vinculado a um cliente ativo');
  }

  const tlCol = window._fbCollection(database, COL, obId, 'timeline');
  const tlSnap = await window._fbGetDocs(tlCol);
  const chunkSize = 400;
  for (let i = 0; i < tlSnap.docs.length; i += chunkSize) {
    const batch = window._fbWriteBatch();
    const slice = tlSnap.docs.slice(i, i + chunkSize);
    for (const d of slice) batch.delete(d.ref);
    await batch.commit();
  }
  await window._fbDeleteDoc(obRef);

  const leadId = String(ob.leadId || '').trim();
  if (leadId) {
    try {
      const leadRef = window._fbDoc(database, 'leads', leadId);
      const leadSnap = await window._fbGetDoc(leadRef);
      if (leadSnap.exists()) {
        const ld = leadSnap.data() || {};
        if (String(ld.onboardingId || '').trim() === obId) {
          const patch = { onboardingId: null, sentToOnboardingAt: null };
          if (String(ld.status || '').trim() === LEAD_STATUS.ONBOARDING) {
            patch.status = LEAD_STATUS.CONTATO;
          }
          await patchLead(
            leadId,
            patch,
            'Vínculo de onboarding removido (onboarding excluído)',
            { onboardingId: obId },
            'edit',
          );
        }
      }
    } catch (e) {
      console.warn('[CRM] deleteOnboardingPermanent: falha ao limpar vínculo do lead', e);
    }
  }
}

/**
 * Conversão manual no CRM (após `convertOnboardingToClientFromCrm` no browser).
 * Fluxos públicos (site / convite) usam `finalizeOnboardingConversionAfterPublicForm` nas Cloud Functions — mesmo efeito em lead + onboarding + `data/main`.
 */
export async function markOnboardingConverted(obId, clientId) {
  const t = nowMs();
  const { uid, name } = actorFromWindow();
  const database = db();
  const ref = window._fbDoc(database, COL, obId);
  const prevSnap = await window._fbGetDoc(ref);
  const prevLeadId = prevSnap.exists() ? String(prevSnap.data()?.leadId || '').trim() : '';

  await window._fbUpdateDoc(ref, {
    status: ONBOARDING_STATUS.CONVERTIDO,
    convertedAt: t,
    convertedClientId: String(clientId || ''),
    updatedAt: t,
  });
  const evCol = window._fbCollection(database, COL, obId, 'timeline');
  await window._fbAddDoc(evCol, {
    kind: 'convert',
    at: window._fbServerTimestamp(),
    actorUid: uid,
    actorName: name,
    message: `Convertido em cliente (${clientId})`,
    meta: { clientId },
  });

  if (prevLeadId) {
    try {
      const lr = window._fbDoc(database, 'leads', prevLeadId);
      const ls = await window._fbGetDoc(lr);
      const already = ls.exists() && String(ls.data()?.convertedClientId || '').trim();
      if (!already) {
        await markLeadConverted(prevLeadId, String(clientId || ''));
      }
    } catch (e) {
      console.warn('[CRM] markOnboardingConverted: lead convertido não atualizado', e);
    }
  }
}

export { ONBOARDING_STATUS, ONBOARDING_STATUS_LABEL, ONBOARDING_STATUS_ORDER, ONBOARDING_ENTRY_SOURCE };
